import apiClient from '@/utility/apiClient'
import { handleApiError } from '@/lib/api/utils/errorHandler'
import { RequestOptions, Reaction, ReactionResponse } from '@/lib/api/types/api'

export interface ContentFilterParams {
  filter?: 'follow' | 'stories' | 'knowhows'
  category?: string
  page?: number
  categoryIds?: string[]
  isFollowing?: boolean
}

/**
 * Content API service
 * Handles content endpoints for multiple content types (stories, knowhows, etc.)
 */
export const ContentService = {
  /**
   * Get all contents with optional filtering
   * @param page Page number (optional)
   * @param categoryIds Category IDs to filter by (optional)
   * @param isFollowing Whether to filter by followed content (optional)
   * @param contentType Content type filter ('stories' or 'knowhows') (optional)
   * @param options Additional request options (optional)
   */
  getContents: async (
    page: number = 1,
    categoryIds: string[] = [],
    isFollowing: boolean = false,
    contentType?: 'stories' | 'knowhows',
    options?: RequestOptions,
  ): Promise<any> => {
    try {
      const params: {
        page: number
        'category_ids[]'?: string[]
        is_following?: boolean
      } = { page }

      // Add category IDs if provided
      if (categoryIds.length > 0) {
        params['category_ids[]'] = categoryIds
      }

      // Add following filter if specified
      if (isFollowing) {
        params.is_following = true
      }

      // Determine the API endpoint based on content type
      let url = 'contents'
      if (contentType === 'stories') {
        url = 'stories'
      } else if (contentType === 'knowhows') {
        url = 'knowhows'
      }

      const response = await apiClient.request('GET', url, {
        ...options,
        params,
      })

      return {
        records: response.data.records || [],
        pageMeta: response.data.pageMeta || {
          currentPage: page,
          totalPages: 1,
          totalContent: response.data.records?.length || 0,
        },
      }
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Get story by ID
   * @param storyId Story ID
   * @param companyId Company ID
   * @param bookId Book ID (optional)
   * @param options Additional request options (optional, groupId can be included)
   */
  getStoryById: async (
    storyId: string,
    companyId: string,
    bookId?: string,
    options?: RequestOptions & { groupId?: string },
  ): Promise<any> => {
    try {
      const { groupId, ...restOptions } = options || {}
      const params: { companyId: string; bookId?: string; groupId?: string } = { companyId }
      if (bookId) {
        params.bookId = bookId
      }
      if (groupId) {
        params.groupId = groupId
      }

      const url = `stories/${storyId}`

      const response = await apiClient.request('GET', url, {
        ...restOptions,
        params,
      })

      // Process tag array for keywords
      const storyData = response.data
      storyData.keywords = storyData.tagArray?.join(',') || ''

      return storyData
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Get knowhow by ID
   * @param knowhowId Knowhow ID
   * @param companyId Company ID
   * @param bookId Book ID (optional)
   * @param options Additional request options (optional, groupId can be included)
   */
  getKnowhowById: async (
    knowhowId: string,
    companyId: string,
    bookId?: string,
    options?: RequestOptions & { groupId?: string },
  ): Promise<any> => {
    try {
      const { groupId, ...restOptions } = options || {}
      const params: { companyId: string; bookId?: string; groupId?: string } = { companyId }
      if (bookId) {
        params.bookId = bookId
      }
      if (groupId) {
        params.groupId = groupId
      }

      const url = `knowhows/${knowhowId}`

      const response = await apiClient.request('GET', url, {
        ...restOptions,
        params,
      })
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  getReactions: async (storyId: string): Promise<ReactionResponse> => {
    const url = `reactions/${storyId}`
    const response = await apiClient.request('GET', url)
    return response.data
  },

  postReaction: async (
    storyId: string,
    reactionId: number,
    memberId?: string,
  ): Promise<Reaction> => {
    const url = `reactions/${storyId}`
    const response = await apiClient.request('POST', url, {
      data: { reaction_id: reactionId },
    })
    return response.data
  },

  deleteReaction: async (storyId: string, reactionId: number): Promise<void> => {
    const url = `reactions/${storyId}`
    await apiClient.request('DELETE', url, {
      data: { reaction_id: reactionId },
    })
  },

  createVisitor: async (): Promise<void> => {
    const url = 'visitors'
    await apiClient.request('POST', url)
  },
}
